import { ThunkAction } from 'redux-thunk';
import { AnyAction } from 'redux';

import type { RootState } from '~/store/types';

import { loginFailure } from '@comeon/analytics';
import { LOGIN_FAIL } from '@comeon/actions-public';
import { requestBankIDToken } from '..';
import { showQueuedNotification } from '~/components/notifications/ducks/actions';

export const BANKID_TOKEN_RECEIVED = '@komigen/mobile-frontend/authentication/BANKID_TOKEN_RECEIVED';
export const BANKID_AUTH_FAILED = '@komigen/mobile-frontend/authentication/BANKID_AUTH_FAILED';
export const INITIATE_BANKID = '@komigen/mobile-frontend/authentication/INITIATE_BANKID';
export const CLEAR_BANKID = '@komigen/mobile-frontend/authentication/CLEAR_BANKID';
export const BANKID_COMPLETE = '@komigen/mobile-frontend/authentication/BANKID_COMPLETE';
export const BANKID_EXTRA_INFO_PROMPT = '@komigen/mobile-frontend/authentication/BANKID_EXTRA_INFO_PROMPT';
export const BANKID_ACCOUNT_LINK_PROMPT = '@komigen/mobile-frontend/authentication/BANKID_ACCOUNT_LINK_PROMPT';
export const BANKID_LOADING = '@komigen/mobile-frontend/authentication/BANKID_LOADING';
export const BANKID_SHOW_COMPLIANCE = '@komigen/mobile-frontend/authentication/BANKID_SHOW_COMPLIANCE';
export const BANKID_UPDATE_ORDER_REF = '@komigen/mobile-frontend/authentication/BANKID_UPDATE_ORDER_REF';
export const BANKID_SESSION_CRITERIA = '@komigen/mobile-frontend/authentication/BANKID_SESSION_CRITERIA';
export const SWISH_INSTALLED_ON_MOBILE = '@komigen/mobile-frontend/authentication/SWISH_INSTALLED_ON_MOBILE';

export const bankIdUpdateSessionCriteria = (payload: unknown) => ({
	type: BANKID_SESSION_CRITERIA,
	payload
});

export const bankIdShowCompliance = () => ({
	type: BANKID_SHOW_COMPLIANCE
});

export const bankIdUpdateOrderRef = (payload: unknown) => ({
	type: BANKID_UPDATE_ORDER_REF,
	payload
});

export const initiatePolling = () => ({
	type: INITIATE_BANKID
});

export const bankIdError = (
	errorCode: string | null = null,
	autoHide = true,
	withinSidebar = false,
	errorDesc: string | null = null,
	isQrMode = false
): ThunkAction<void, RootState, undefined, AnyAction> => {
	return (dispatch, getState) => {
		const { translations } = getState() as { translations: Record<string, string> };
		const code = errorCode ?? 'BANKID_GENERAL_ERROR';

		const msg = (!!errorCode && translations[errorCode]) || errorDesc || translations.BANKID_GENERAL_ERROR;
		loginFailure({
			method: isQrMode ? 'qr-mode' : 'BankID',
			oldEvent: true
		});
		loginFailure({
			method: isQrMode ? 'qr-mode' : 'BankID',
			newEvent: true,
			error: `${code} - ${msg}`
		});
		if (code === 'BANKID_ACC_LOCKED') {
			dispatch({
				type: LOGIN_FAIL,
				payload: { errorCode: code, withinSidebar }
			});
			return;
		}
		if (code === 'VALIDATION_ERROR_EMAIL') {
			dispatch(
				showQueuedNotification({
					message: msg,
					color: 'red',
					autoHide: true,
					autoHideDuration: 8000,
					openInSidebar: withinSidebar
				})
			);
			return;
		}
		dispatch(
			showQueuedNotification({
				message: msg,
				color: 'red',
				autoHide,
				openInSidebar: withinSidebar
			})
		);
	};
};

export const getBankIDToken = (
	startPolling = true,
	qrMode?: object
): ThunkAction<Promise<object>, RootState, undefined, AnyAction> => {
	return async dispatch => {
		try {
			const response = (await requestBankIDToken(qrMode)) as { error?: { errorDescription: string } };

			if (response.error) {
				// TODO: cleanup, error desc comes from BE, so adding a  param to not look for translation. Hacky.
				dispatch(bankIdError(null, false, true, response.error.errorDescription));
				return dispatch({
					type: BANKID_AUTH_FAILED
				});
			}
			dispatch({
				type: BANKID_TOKEN_RECEIVED,
				payload: { ...response, ...qrMode }
			});
			if (startPolling) {
				return dispatch({ type: INITIATE_BANKID });
			}
			return response;
		} catch (e) {
			return dispatch({
				type: BANKID_AUTH_FAILED
			});
		}
	};
};

export const clearBankID = () => ({
	type: CLEAR_BANKID
});

export const bankIdExtraInfoPrompt = (payload: unknown) => ({ type: BANKID_EXTRA_INFO_PROMPT, payload });

export const bankIdAccountLinkPrompt = (payload: unknown) => ({ type: BANKID_ACCOUNT_LINK_PROMPT, payload });

export const bankIdLoading = (payload = true) => ({
	type: BANKID_LOADING,
	payload
});

export const setSwishInstalledOnMobile = (payload: boolean) => ({ type: SWISH_INSTALLED_ON_MOBILE, payload });
